import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

const OurTeam = () => {
    // Placeholder team members - you can update this with actual content
    const teamMembers = [
        {
            name: "Jamshed",
            position: "Co-Founder",
            image: null, // Placeholder for actual photo
            description: "Visionary leader committed to blending academic excellence with Islamic values."
        },
        {
            name: "Saad",
            position: "Co-Founder",
            image: null, // Placeholder for actual photo
            description: "Educational innovator focused on creating nurturing learning environments."
        },
        {
            name: "Asima",
            position: "Co-Founder",
            image: null, // Placeholder for actual photo
            description: "Community development expert passionate about holistic child development."
        }
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section */}
            <section className="relative flex items-center justify-center min-h-screen overflow-hidden">
                <div
                    className="absolute inset-0 transform scale-110 bg-center bg-no-repeat bg-cover"
                    style={{
                        backgroundImage: `url('${require('../../assets/tutor-with-litthe-girl-studying-home.jpg')}')`
                    }}
                />
                <div className="absolute inset-0 bg-black/60" />

                <div className="relative z-10 max-w-4xl px-4 mx-auto text-center text-white">
                    <motion.h1
                        className="mb-6 text-5xl font-bold md:text-7xl"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                        FOUNDERS' <span className="text-yellow-400">VISION</span>
                    </motion.h1>

                    <motion.p
                        className="mb-8 text-xl text-gray-200 md:text-2xl"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                    >
                        Rooted in Purpose. Driven by Values.
                    </motion.p>
                </div>
            </section>

            {/* Introduction Section */}
            <section className="py-20 bg-white">
                <div className="px-4 mx-auto max-w-7xl">
                    <div className="text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="max-w-4xl mx-auto leading-relaxed text-gray-700"
                        >
                            <p className="mb-6 text-lg">
                                Aureus Cambridge School was founded by a team of passionate individuals with a shared belief: that education should shape not just the intellect, but the soul.
                            </p>
                            <p className="mb-6 text-lg">
                                Our founders bring experience from diverse fields: education, entrepreneurship and community development, yet they are united by a single vision:
                            </p>
                            <p className="mb-6 text-xl font-semibold text-gray-900">
                                To raise a generation that is academically strong, morally grounded and spiritually aware.
                            </p>
                            <p className="text-lg">
                                They envisioned a school that blends global academic standards with Islamic teachings, where children thrive in both Dunyah O Akhirah.
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Team Members Section */}
            <section className="py-20 bg-white">
                <div className="px-4 mx-auto max-w-7xl">
                    <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                        {teamMembers.map((member, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="overflow-hidden transition-shadow duration-300 bg-gray-50 rounded-2xl hover:shadow-lg"
                            >
                                <div className="relative h-64 overflow-hidden">
                                    {member.image ? (
                                        <img
                                            src={member.image}
                                            alt={member.name}
                                            className="object-cover w-full h-full"
                                        />
                                    ) : (
                                        <div className="flex items-center justify-center w-full h-full bg-gray-200">
                                            <div className="text-center">
                                                <div className="w-16 h-16 mx-auto mb-3 bg-gray-300 rounded-full"></div>
                                                <p className="text-sm text-gray-500">Photo Coming Soon</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="p-6">
                                    <h3 className="mb-2 text-xl font-bold text-gray-900">
                                        {member.name}
                                    </h3>
                                    <p className="mb-3 font-semibold text-yellow-600">
                                        {member.position}
                                    </p>
                                    <p className="leading-relaxed text-gray-600">
                                        {member.description}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Team Values Section */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-6xl px-4 mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="mb-16 text-center"
                    >
                        <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
                            What Sets Us <span className="text-yellow-500">Apart</span>
                        </h2>
                        <p className="max-w-3xl mx-auto text-xl text-gray-600">
                            Rather than replicating traditional models, our founders built Aureus on three guiding principles
                        </p>
                    </motion.div>

                    <div className="grid gap-8 md:grid-cols-3">
                        {[
                            {
                                title: "Academic Excellence",
                                description: "Aligned with the Cambridge curriculum to ensure world-class educational standards and global recognition."
                            },
                            {
                                title: "Spiritual & Moral Development",
                                description: "Through Islamic Tarbiyah and Quranic understanding, nurturing the soul alongside the mind."
                            },
                            {
                                title: "Personalised Learning",
                                description: "In a nurturing, low stress environment where every child's unique potential is recognized and developed."
                            }
                        ].map((value, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="p-8 bg-white shadow-lg rounded-2xl"
                            >
                                <h3 className="mb-4 text-xl font-bold text-gray-900">{value.title}</h3>
                                <p className="leading-relaxed text-gray-600">{value.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-20 bg-yellow-400">
                <div className="max-w-4xl px-4 mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="mb-8 text-3xl font-bold text-black md:text-4xl">
                            Experience Our Vision
                        </h2>
                        <p className="max-w-2xl mx-auto mb-8 text-xl text-black">
                            This vision shapes every decision—from curriculum to classroom culture—and ensures that Aureus remains a school of purpose, not just performance.
                        </p>
                        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                            <Link
                                to="/contact"
                                className="inline-flex items-center justify-center w-full px-8 py-4 text-lg font-semibold text-white transition-colors duration-300 bg-gray-900 rounded-full sm:w-auto hover:bg-gray-800 group"
                            >
                                Learn More
                                <ChevronRightIcon className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                            <Link
                                to="/apply-online"
                                className="inline-flex items-center justify-center w-full px-8 py-4 text-lg font-semibold text-gray-900 transition-colors duration-300 border-2 border-gray-900 rounded-full sm:w-auto hover:bg-gray-900 hover:text-white"
                            >
                                Join Our Vision
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default OurTeam;
